import { DatePicker } from 'antd';
import { pathOr, prop, sortBy } from 'ramda';
import { useMemo, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';

import {
  styledComponentsTheme,
  OldTable,
  AvatarWithName,
  Button,
  ExpandIcon,
  Flex,
  Heading,
  IconButton,
  Input,
  Text,
  SelectComponents,
} from '@frontend/legacy-components';
import { useDateFormat } from '@frontend/utils';

import Select from 'components/lib2/Select2';

import { open as openWorkflowStatusOverviewModal } from 'modules/common/modals/workflowStatusOverviewModal/data/actions';
import { TeamMemberSelect } from 'modules/common/smartComponents/TeamMemberSelect';
import { AccountSelector } from 'modules/common/smartComponents/accountSelector/AccountSelector';

import { formatPercent } from 'utils/formatters';

import { getIsLoading, getQuotes, getQuoteWorkflow } from './data/reducer';

const { RangePicker } = DatePicker;

const SORT_BOTH = ['ascending', 'descending'];

const COMPLETE = 'Complete';
const IN_PROGRESS = 'In Progress';
const NOT_STARTED = 'Not Started';
const ON_HOLD = 'On Hold';
const NO_QUOTE = 'No Quote';

const STATUS_LIST = [
  { value: 'completed', label: COMPLETE },
  { value: 'in_progress', label: IN_PROGRESS },
  { value: 'no_quote', label: NO_QUOTE },
  { value: 'not_started', label: NOT_STARTED },
  { value: 'on_hold', label: ON_HOLD },
];

const MultiSelect = ({
  value,
  onChange,
  options,
  placeholder,
  disabled,
  autoFocus,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  return (
    <Select
      autoFocus={autoFocus}
      closeMenuOnSelect={false}
      disabled={disabled}
      inputValue={searchTerm}
      isClearable={false}
      isMulti={true}
      onChange={onChange}
      onInputChange={setSearchTerm}
      options={options}
      placeholder={placeholder}
      value={value}
    />
  );
};

const StatusLabel = styled.p`
  font-weight: 600;
  ${(props) =>
    props.status == COMPLETE &&
    `color: ${styledComponentsTheme.color.status.complete};`};
  ${(props) =>
    props.status == IN_PROGRESS &&
    `color: ${styledComponentsTheme.color.status.inProgress};`};
  ${(props) =>
    props.status == NO_QUOTE &&
    `color: ${styledComponentsTheme.color.status.notApplicable};`};
  ${(props) =>
    props.status == NOT_STARTED &&
    `color: ${styledComponentsTheme.color.status.notStarted};`};
  ${(props) =>
    props.status == ON_HOLD &&
    `color: ${styledComponentsTheme.color.status.onHold};`};
`;

const Placeholder = () => <Text color="feature.placeholder">---</Text>;

const AccountComponent = (contact) =>
  pathOr(<Placeholder />, ['account', 'name'], contact);

const DateComponent = (dateTime) => {
  const { toDateString } = useDateFormat();
  if (!dateTime) {
    return <Placeholder />;
  } else {
    return toDateString(dateTime);
  }
};

const TeammateComponent = (teamMember) => {
  const fullName = teamMember
    ? !teamMember.isDeleted
      ? `${teamMember.firstName} ${teamMember.lastName}`
      : teamMember.email
    : undefined;

  return (
    <AvatarWithName
      name={fullName}
      isDeleted={teamMember?.isDeleted ?? false}
      backgroundColor={teamMember?.avatarColor}
    />
  );
};

const getQuoteNumberComponent = (onClick) => {
  const component = (quoteNumber, quote) => {
    return (
      <Flex justify="space-between" align="center" wrap="nowrap">
        <Button
          size="md"
          variant="link"
          as="a"
          href={`quotes/edit/${quoteNumber}`}
        >
          {quoteNumber}
        </Button>
        <IconButton
          ml="auto"
          size="sm"
          onClick={() => {
            onClick(quoteNumber, quote.id);
          }}
        >
          <ExpandIcon />
        </IconButton>
      </Flex>
    );
  };
  component.displayName = 'QuoteNumberComponent';
  return component;
};

const PriorityComponent = (priority) =>
  priority == null ? <Placeholder /> : priority;

const QuoteStatusComponent = (
  workflowCompletionPercentage,
  { workflowCompletionStatus },
) => {
  let status = STATUS_LIST.find(
    ({ value }) => value === workflowCompletionStatus,
  );

  status = (status && status.label) || NOT_STARTED;

  return (
    <StatusLabel status={status}>
      {status == IN_PROGRESS
        ? `${status} (${formatPercent(workflowCompletionPercentage, 0)})`
        : status}
    </StatusLabel>
  );
};

const RfqNumberComponent = (rfqNumber) =>
  rfqNumber == null ? <Placeholder /> : rfqNumber;

const WorkflowStatusComponent = (workflowStep) => {
  let status = STATUS_LIST.find(
    ({ value }) => value === workflowStep.completionStatus,
  );

  status = (status && status.label) || NOT_STARTED;

  return (
    <StatusLabel status={status}>
      {status === IN_PROGRESS
        ? `${status} (${formatPercent(workflowStep.completionPercentage, 0)})`
        : status}
    </StatusLabel>
  );
};

const AccountSelectorContainer = ({ innerProps, ...restProps }) => {
  return (
    <SelectComponents.SelectContainer
      innerProps={{ ...innerProps, width: '20rem' }}
      {...restProps}
    />
  );
};

const useTableConfig = ({
  isPriorityEnabled,
  onQuoteNumberClick,
  workflowSteps = [],
}) => {
  const { dateFormat } = useDateFormat();
  return useMemo(() => {
    const accountColumn = {
      dataIndex: 'contact',
      key: 'contact',
      render: AccountComponent,
      title: 'Account',
      filterOptions: {
        sortDirections: SORT_BOTH,
        formatter: (selectValue) => selectValue?.map(({ id }) => id),
        filterInputComponent: (
          <AccountSelector
            components={{ SelectContainer: AccountSelectorContainer }}
            isMulti
            allowUnassignedOption
          />
        ),
      },
    };

    const createdColumn = {
      dataIndex: 'created',
      key: 'created',
      render: DateComponent,
      title: 'Created',
      filterOptions: {
        sortDirections: SORT_BOTH,
        filterInputComponent: (
          <RangePicker format={dateFormat} style={{ width: '15rem' }} />
        ),
      },
    };

    const dueDateColumn = {
      dataIndex: 'dueDate',
      key: 'dueDate',
      render: DateComponent,
      title: 'Date Due',
      filterOptions: {
        sortDirections: SORT_BOTH,
        filterInputComponent: (
          <RangePicker format={dateFormat} style={{ width: '15rem' }} />
        ),
      },
    };

    const salespersonColumn = {
      dataIndex: 'salesperson',
      key: 'salesperson',
      render: TeammateComponent,
      title: 'Salesperson',
      filterOptions: {
        sortDirections: SORT_BOTH,
        formatter: (selectValue) => selectValue?.map(({ value }) => value),
        filterInputComponent: (
          <TeamMemberSelect isMulti mode="assignmentFilter" />
        ),
      },
    };

    const estimatorColumn = {
      dataIndex: 'estimator',
      key: 'estimator',
      render: TeammateComponent,
      title: 'Estimator',
      filterOptions: {
        sortDirections: SORT_BOTH,
        formatter: (selectValue) => selectValue?.map(({ value }) => value),
        filterInputComponent: (
          <TeamMemberSelect isMulti mode="assignmentFilter" />
        ),
      },
    };

    const quoteNumberColumn = (onClick) => ({
      dataIndex: 'numberWithRevision',
      fixed: 'left',
      key: 'number',
      render: getQuoteNumberComponent(onClick),
      title: 'Quote',
      filterOptions: {
        sortDirections: SORT_BOTH,
        filterInputComponent: <Input size="sm" />,
      },
    });

    const priorityColumn = {
      dataIndex: 'priority',
      fixed: 'left',
      key: 'priority',
      render: PriorityComponent,
      title: 'Priority',
      filterOptions: {
        sortDirections: SORT_BOTH,
        empty: [],
        filterInputComponent: (
          <MultiSelect
            options={[
              { label: 'No Priority', value: 0 },
              ...Array.from({ length: 10 }, (_, i) => ({
                label: i + 1,
                value: i + 1,
              })),
            ]}
          />
        ),
      },
    };

    const quoteStatusColumn = {
      dataIndex: 'workflowCompletionPercentage',
      fixed: 'left',
      key: 'status',
      render: QuoteStatusComponent,
      title: 'Status',
      filterOptions: {
        sortDirections: SORT_BOTH,
        empty: [],
        filterInputComponent: <MultiSelect options={STATUS_LIST} />,
      },
    };

    const rfqNumberColumn = {
      dataIndex: 'rfqNumber',
      fixed: 'left',
      key: 'rfqNumber',
      render: RfqNumberComponent,
      title: 'RFQ #',
      filterOptions: {
        sortDirections: SORT_BOTH,
        filterInputComponent: <Input size="sm" />,
      },
    };

    const config = [quoteNumberColumn(onQuoteNumberClick), rfqNumberColumn];

    if (isPriorityEnabled) {
      config.push(priorityColumn);
    }

    config.push(
      quoteStatusColumn,
      accountColumn,
      createdColumn,
      dueDateColumn,
      salespersonColumn,
      estimatorColumn,
    );

    workflowSteps.forEach((step) => {
      config.push({
        title: pathOr('--', ['step', 'name'], step),
        key: pathOr('--', ['step', 'name'], step),
        dataIndex: [
          'quoteItemWorkflowSteps',
          pathOr('', ['step', 'locator'], step),
        ],
        render: WorkflowStatusComponent,
        filterOptions: {
          sortDirections: SORT_BOTH,
          empty: [],
          filterInputComponent: <MultiSelect options={STATUS_LIST} />,
        },
      });
    });

    return config;
  }, [dateFormat, isPriorityEnabled, onQuoteNumberClick, workflowSteps]);
};

const sortByPosition = sortBy(prop('position'));

const WorkflowTable = ({ isPriorityEnabled, onChange, pagination }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const isLoading = useSelector(getIsLoading);
  const quotes = useSelector(getQuotes);
  const quoteWorkflow = useSelector(getQuoteWorkflow);

  const dispatchOpenWorkflowStatusModal = useCallback(
    ({ quoteId, callback }) =>
      dispatch(openWorkflowStatusOverviewModal.request({ quoteId, callback })),
    [dispatch],
  );

  const openWorkflowStatusOverviewModalAction = useCallback(
    (quoteId, quoteNumber) => {
      dispatch(
        dispatchOpenWorkflowStatusModal({
          quoteId: quoteId,
          callback: (quoteItemId) => {
            history.push(
              `quotes/edit/${quoteNumber}?quote_item=${quoteItemId}`,
            );
          },
        }),
      );
    },
    [dispatch, dispatchOpenWorkflowStatusModal, history],
  );

  const onQuoteNumberClick = useCallback(
    (quoteNumber, quoteId) =>
      openWorkflowStatusOverviewModalAction(quoteId, quoteNumber),
    [openWorkflowStatusOverviewModalAction],
  );

  const workflowSteps = useMemo(
    () => sortByPosition(pathOr([], ['steps'], quoteWorkflow)),
    [quoteWorkflow],
  );

  const config = useTableConfig({
    isPriorityEnabled,
    onQuoteNumberClick,
    workflowSteps,
  });

  return (
    <OldTable
      rowKey="id"
      className="workflow-table"
      columns={config}
      data={quotes}
      loading={isLoading}
      emptyOptions={{
        emptyLabel: 'No quotes in draft',
      }}
      headerOptions={{
        leftComponent: <Heading size="lg">workflows</Heading>,
      }}
      onChange={onChange}
      paginationOptions={{
        itemsPerPage: pagination.pageSize,
        totalItems: pagination.totalCount,
      }}
    />
  );
};

export default WorkflowTable;
